import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header/Header";

import ListMenu from "./ListMenu/ListMenu";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import useSize from "../../../../Utils/useSize/useSize";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";

import SummaryCard from "./SummaryCard";
import Total from "./Total/Total";

import moment from "moment/moment";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const fmt = require("indian-number-format");

const backend = await new Backend().init();

export default function Summary() {
  const windowSize = useSize();

  const userType = Number(localStorage.getItem("user_type") || 7);

  const [loading, setLoading] = useState(false);

  const [user, setUser] = useState([]);

  const [summaryData, setSummaryData] = useState({});

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = () => {
    setLoading(true);
    let data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      admin_id: localStorage.getItem("id"),
    };
    backend.load_summary_super(data).then((r) => {
      setLoading(false);
      if (r.error == "False") {
        setUser(r.user);
      } else {
        if (r.logout == 1) {
          window.LogOut();
        } else {
          toast.error(r.message);
        }
      }
    });
  };

  const downloadStandingPDF = async () => {
    try {
      // ✅ Proper pdfMake import (safe for Vite/CRA)
      const pdfMakeModule = await import("pdfmake/build/pdfmake");
      const pdfFontsModule = await import("pdfmake/build/vfs_fonts");

      const pdfMake = pdfMakeModule.default;
      pdfMake.vfs = pdfFontsModule.default.vfs;

      const data = Object.values(summaryData);

      if (!data || data.length === 0) {
        toast.error("No data available to export");
        return;
      }

      // ✅ Build table body safely (9 columns total)
      const body = [
        [
          { text: "Username", style: "tableHeader" },
          { text: "Booked", style: "tableHeader" },
          { text: "Live P&L", style: "tableHeader" },
          { text: "Net Live P&L", style: "tableHeader" },
          { text: "Admin Sharing", style: "tableHeader" },
          { text: "Super Sharing", style: "tableHeader" },
          { text: "Admin Live P&L", style: "tableHeader" },
          { text: "Super Live P&L", style: "tableHeader" },
        ],

        ...data.map((i) => [
          { text: i.username ?? "-", style: "tableCell" },

          {
            text: fmt.format(i.booked || 0),
            style: "numericCell",
            color: parseFloat(i.booked || 0) >= 0 ? "#399918" : "#FF0000",
          },

          {
            text: fmt.format(i.total || 0),
            style: "numericCell",
            color: parseFloat(i.total || 0) >= 0 ? "#399918" : "#FF0000",
          },

          {
            text: fmt.format(i.net_total || 0),
            style: "numericCell",
            color: parseFloat(i.net_total || 0) >= 0 ? "#399918" : "#FF0000",
          },

          {
            text: fmt.format(i.self || 0),
            style: "numericCell",
            color: parseFloat(i.self || 0) >= 0 ? "#399918" : "#FF0000",
          },

          {
            text: fmt.format(i.lower || 0),
            style: "numericCell",
            color: parseFloat(i.lower || 0) >= 0 ? "#399918" : "#FF0000",
          },

          {
            text: fmt.format(i.selfLive || 0),
            style: "numericCell",
            color: parseFloat(i.selfLive || 0) >= 0 ? "#399918" : "#FF0000",
          },

          {
            text: fmt.format(i.lowerLive || 0),
            style: "numericCell",
            color: parseFloat(i.lowerLive || 0) >= 0 ? "#399918" : "#FF0000",
          },
        ]),
      ];

      // ✅ Add TOTAL row safely
      if (totalSummary) {
        body.push([
          { text: "TOTAL", style: "totalLabel" },

          { text: fmt.format(totalSummary.booked || 0), style: "totalCell" },
          { text: fmt.format(totalSummary.total || 0), style: "totalCell" },
          { text: fmt.format(totalSummary.net_total || 0), style: "totalCell" },
          { text: fmt.format(totalSummary.self || 0), style: "totalCell" },
          { text: fmt.format(totalSummary.lower || 0), style: "totalCell" },
          { text: fmt.format(totalSummary.selfLive || 0), style: "totalCell" },
          { text: fmt.format(totalSummary.lowerLive || 0), style: "totalCell" },
        ]);
      }

      // ✅ Safe logo handling
      const logoSection = window.LOGO
        ? [
            {
              image: "logo",
              width: 60,
            },
          ]
        : [];

      const docDefinition = {
        pageSize: "A3",
        pageOrientation: "landscape",
        pageMargins: [40, 60, 40, 60],

        content: [
          {
            columns: [
              ...logoSection,
              {
                text: `${window.APP_NAME || "APP"} SUPER REPORT`,
                style: "header",
                width: "*",
                alignment: "right",
              },
            ],
          },

          {
            text: `Generated on: ${moment().format("DD-MMM-YYYY HH:mm")}`,
            style: "subHeader",
          },

          {
            table: {
              headerRows: 1,
              widths: Array(body[0].length).fill("auto"), // ✅ Dynamic safe widths
              body,
            },

            layout: {
              fillColor: (rowIndex) =>
                rowIndex === 0
                  ? "#1E293B"
                  : rowIndex % 2 === 0
                    ? "#F8FAFC"
                    : null,
            },
          },

          {
            text: `\nThank you for using ${window.APP_NAME || "our platform"}.`,
            style: "footerNote",
          },
        ],

        images: window.LOGO
          ? {
              logo: window.LOGO,
            }
          : {},

        styles: {
          header: {
            fontSize: 18,
            bold: true,
            margin: [0, 0, 0, 10],
          },

          subHeader: {
            fontSize: 10,
            color: "#64748B",
            margin: [0, 0, 0, 10],
          },

          tableHeader: {
            bold: true,
            fontSize: 11,
            color: "#FFFFFF",
            fillColor: "#1E293B",
            alignment: "center",
          },

          tableCell: {
            fontSize: 10,
            margin: [0, 5, 0, 5],
          },

          numericCell: {
            fontSize: 10,
            margin: [0, 5, 0, 5],
            alignment: "right",
          },

          totalLabel: {
            bold: true,
            fontSize: 11,
            color: "#FFFFFF",
            fillColor: "#334155",
          },

          totalCell: {
            bold: true,
            fontSize: 10,
            color: "#FFFFFF",
            fillColor: "#475569",
            alignment: "right",
          },

          footerNote: {
            fontSize: 9,
            italics: true,
            alignment: "center",
            margin: [0, 15, 0, 0],
          },
        },

        footer: (currentPage, pageCount) => ({
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: "center",
          fontSize: 9,
        }),
      };

      pdfMake
        .createPdf(docDefinition)
        .download(`${moment().format("DD-MMM")}-Super-Summary.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    }
  };

  const downloadExcel = () => {
    if (!summaryData || Object.keys(summaryData).length === 0) {
      toast.error("No data to export");
      return;
    }

    const data = Object.values(summaryData).map((item) => ({
      Username: item.username,
      Booked: item.booked,
      "Live Total": item.total,
      "Net Live Total": item.net_total,
      "Self Share": item.shares?.self ?? 0,
      "Lower Share": item.shares?.lower ?? 0,
      "Self Live": item.selfLive,
      "Lower Live": item.lowerLive,
    }));

    // ✅ Add total row at the bottom
    if (totalSummary) {
      data.push({
        Username: "TOTAL",
        Booked: totalSummary.booked,
        "Live Total": totalSummary.total,
        "Net Live Total": totalSummary.net_total,
        "Self (Combined)": totalSummary.self,
        "Lower (Combined)": totalSummary.lower,
        "Self Live": totalSummary.selfLive,
        "Lower Live": totalSummary.lowerLive,
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(data, { origin: "A1" });

    // ✅ Bold the TOTAL row
    const totalRowIndex = data.length + 1;
    const totalRowCell = `A${totalRowIndex}`;
    if (!worksheet[totalRowCell].s) worksheet[totalRowCell].s = {};
    worksheet[totalRowCell].s = {
      font: { bold: true, color: { rgb: "0182f7" } },
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(blob, `Super-Summary-${moment().format("DD-MMM-YYYY")}.xlsx`);
  };

  const handleSummaryCalculated = (data) => {
    setSummaryData((prev) => ({
      ...prev,
      [data.id]: data,
    }));
  };

  // ✅ Compute total of all SummaryCard values
  const totalSummary = useMemo(() => {
    const all = Object.values(summaryData);
    if (all.length === 0) return null;

    const sum = (key) =>
      all.reduce(
        (acc, curr) => parseFloat(acc) + parseFloat(curr[key] || 0),
        0,
      );

    return {
      booked: sum("booked"),
      total: sum("total"),
      net_total: sum("net_total"),
      self: sum("self"),
      lower: sum("lower"),
      selfLive: sum("selfLive"),
      lowerLive: sum("lowerLive"),
    };
  }, [summaryData]);

  return (
    <div>
      <Header
        records={user.length}
        downloadStandingPDF={downloadStandingPDF}
        downloadExcel={downloadExcel}
      />
      <div id="modal" className="modal">
        <ListMenu userType={userType} />

        <div
          style={{
            width: windowSize[0],
            height: windowSize[1] - 220,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <div className="loadingContainer">
              <Loading height={windowSize[1] - 220} />
            </div>
          ) : user.length > 0 ? (
            <div
              style={{
                height: windowSize[1] - 220,
                width: windowSize[0],
                overflowY: "scroll",
              }}
            >
              {user.map((item, index) => {
                return (
                  <SummaryCard
                    userType={userType}
                    key={item.super_id}
                    item={item}
                    index={index}
                    onCalculated={handleSummaryCalculated}
                  />
                );
              })}
            </div>
          ) : (
            <NotFound
              height={windowSize[1] - 220}
              title={"Nothing here"}
              subtext={"No User Summary Found."}
            />
          )}
        </div>

        <Total totalSummary={totalSummary} userType={userType} />
      </div>
    </div>
  );
}
